import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Building2, ArrowRight, Plus, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/services/auth/authContext';
import { apiClient } from '@/services/api/apiClient';

interface RestaurantInfo {
  rest_id: number;
  rest_name: string;
  rest_account_id: number;
  subscription_plan: string;
  is_trial: boolean;
  can_add_locations: boolean;
  locations_used: number;
  locations_available: number;
}

const ExistingRestaurantRedirect = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [restaurantInfo, setRestaurantInfo] = useState<RestaurantInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRestaurantInfo();
  }, []);

  const fetchRestaurantInfo = async () => {
    try {
      const response = await apiClient.get('/restaurants/profile');
      const data = response.data;
      
      // Extract restaurant information
      const info: RestaurantInfo = {
        rest_id: data.rest_id,
        rest_name: data.rest_nm || data.restaurant_name,
        rest_account_id: data.rest_account_id,
        subscription_plan: data.subscription_plan || 'yummy',
        is_trial: data.is_trial || false,
        can_add_locations: data.subscription_plan === 'delicious' || data.subscription_plan === 'gourmet',
        locations_used: data.locations_used || 1,
        locations_available: data.subscription_plan === 'gourmet' ? 4 : 
                            data.subscription_plan === 'delicious' ? 2 : 1
      };
      
      setRestaurantInfo(info);
    } catch (error) {
      console.error('Error fetching restaurant info:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanBadgeColor = (plan: string) => {
    switch(plan?.toLowerCase()) {
      case 'gourmet':
        return 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border-purple-400/30';
      case 'delicious':
        return 'bg-blue-500/20 text-blue-300 border-blue-400/30';
      default:
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl w-full"
      >
        {/* Success Icon */}
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="flex justify-center mb-8"
        >
          <div className="relative">
            <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-400" />
            </div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4 }}
              className="absolute -top-2 -right-2"
            >
              <div className="w-8 h-8 bg-dineai-yellow rounded-full flex items-center justify-center">
                <span className="text-black text-xs font-bold">✓</span>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
          <h1 className="text-3xl font-bold text-white text-center mb-4">
            Welcome Back{user?.first_name ? `, ${user.first_name}` : ''}!
          </h1>
          
          <p className="text-white/80 text-center mb-8">
            Your email is already associated with an existing restaurant
          </p>

          {/* Current Restaurant Card */}
          {restaurantInfo && (
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/5 rounded-xl p-6 mb-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-dineai-yellow" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      {restaurantInfo.rest_name}
                    </h3>
                    <p className="text-white/60 text-sm">
                      Restaurant ID: {restaurantInfo.rest_id}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPlanBadgeColor(restaurantInfo.subscription_plan)}`}>
                    {restaurantInfo.subscription_plan === 'gourmet' && <Crown className="w-3 h-3 inline mr-1" />}
                    {restaurantInfo.subscription_plan.charAt(0).toUpperCase() + restaurantInfo.subscription_plan.slice(1)}
                    {restaurantInfo.is_trial && ' (Trial)'}
                  </span>
                </div>
              </div>

              {/* Location Info */}
              {restaurantInfo.can_add_locations && (
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-white/80 text-sm mb-2">
                    Location Management
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-white/60 text-sm">
                      Using {restaurantInfo.locations_used} of {restaurantInfo.locations_available} locations
                    </span>
                    {restaurantInfo.locations_used < restaurantInfo.locations_available && (
                      <span className="text-green-400 text-xs font-medium">
                        {restaurantInfo.locations_available - restaurantInfo.locations_used} available
                      </span>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Action Buttons */}
          <div className="space-y-4">
            <Button
              onClick={() => navigate('/restaurant-dashboard')}
              className="w-full bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-6 text-lg font-bold rounded-xl flex items-center justify-center gap-2"
            >
              Go to Dashboard
              <ArrowRight className="w-5 h-5" />
            </Button>

            {restaurantInfo?.can_add_locations && restaurantInfo.locations_used < restaurantInfo.locations_available && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  onClick={() => navigate('/restaurant-settings')}
                  variant="outline"
                  className="w-full bg-transparent border-2 border-white/30 text-white hover:bg-white/10 py-6 text-lg rounded-xl flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Another Location
                </Button>
                <p className="text-white/50 text-xs text-center mt-2">
                  Your {restaurantInfo.subscription_plan} plan supports up to {restaurantInfo.locations_available} locations
                </p>
              </motion.div>
            )}
          </div>

          {/* Info Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 p-4 bg-white/5 rounded-lg"
          >
            <p className="text-white/70 text-sm">
              <strong className="text-white">Tip:</strong> {restaurantInfo?.can_add_locations ? 
                `As a ${restaurantInfo.subscription_plan} subscriber, you can manage multiple restaurant locations from your dashboard.` :
                'Upgrade to Delicious or Gourmet to add and manage multiple restaurant locations.'
              }
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default ExistingRestaurantRedirect;